import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
} from "@mui/material";
import { GisMapWrapper } from "@gis/GisMapWrapper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@store/store";
import MapPanel from "./MapPanel";
import { ApplicantDetailsPreviewResponse } from "../application-preview/types";
import { ApplicantDetailsResponse } from "../application-details/types";
import { setActiveStep } from "@modules/stepper/redux/stepperSlice";
import { useNavigate } from "react-router-dom";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import { resetApplicationSubmitted } from "../application-details/redux/applicationSubmittedSlice";

export const AmenityLocationSection: React.FC<{
  isExpanded: boolean;
  toggleExpand: () => void;
  visible?: boolean;
  data: ApplicantDetailsPreviewResponse | ApplicantDetailsResponse;
}> = ({ isExpanded, toggleExpand, data, visible = true }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const mapCoordinates = useMemo(
    () => ({
      latitude: data?.latitude ?? "",
      longitude: data?.longitude ?? "",
    }),
    [data?.latitude, data?.longitude],
  );

  if (!data) return null;

  const {
    application_category,
    national_highway_number,
    state,
    district,
    pincode,
    khasra_number,
    address_1,
    address_2,
    highway_category,
    latitude,
    longitude,
    highway_side,
    chainage,
    start_chainage,
    end_chainage,
    application_code,
    application_status,
    application_stage,
    taluka,
    village,
    piu_office_data,
    ro_office_data,
    other_property_type,
    // application_date,
    // next_steps,
  } = data;
  const displayValue = (value: string | number | null) => value ?? "N/A";
  const startChainageValue = start_chainage || "";
  const endChainageValue = end_chainage || "";
  const hasStartChainage = Boolean(startChainageValue);
  const hasEndChainage = Boolean(endChainageValue);
  const hasBothChainages = hasStartChainage && hasEndChainage;
  const chainageDisplay = startChainageValue;

  const chainageDetailRows = hasBothChainages
    ? [
        { label: "Start Chainage", value: displayValue(startChainageValue) },
        { label: "End Chainage", value: displayValue(endChainageValue) },
      ]
    : [{ label: "Chainage", value: displayValue(chainageDisplay) }];

  const amenityDetails = [
    { label: "Type of Amenity", value: displayValue(application_category) },
    ...(other_property_type
      ? [
          {
            label: "Other Property Type",
            value: displayValue(other_property_type),
          },
        ]
      : []),
    { label: "Highway Category", value: displayValue(highway_category) },
    { label: "Highway Number", value: displayValue(national_highway_number) },
    { label: "Side of Highway", value: displayValue(highway_side) },
    ...chainageDetailRows,
    { label: "Address Line 1", value: displayValue(address_1) },
    { label: "Address Line 2", value: displayValue(address_2) },
    {
      label: "Khasra No. / Block No. / Survey No. / Gat No.",
      value: displayValue(khasra_number),
    },
    { label: "Village", value: displayValue(village) },
    { label: "Taluka", value: displayValue(taluka) },
    { label: "District", value: displayValue(district) },
    { label: "State", value: displayValue(state) },
    { label: "Pincode", value: displayValue(pincode) },
    {
      label: "Project Implementation Unit Office",
      value: displayValue(
        piu_office_data?.map((data: any) => data?.office).join(", ") || "-",
      ),
    },
    { label: "RO Office", value: displayValue(ro_office_data?.office || "-") },
  ];

  const detailsLayout = amenityDetails.reduce(
    (
      rows: Array<Array<{ label: string; value: string | number }>>,
      detail,
      index,
    ) => {
      if (index % 2 === 0) {
        rows.push([detail]);
      } else {
        rows[rows.length - 1].push(detail);
      }
      return rows;
    },
    [],
  );

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <Card sx={{ boxShadow: "none", border: "1px solid #E7E8E9" }}>
        {/* Header */}
        <Box
          sx={{
            backgroundColor: "#F4F2F7",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #E7E8E9",
            pl: 2,
            py: "5px",
          }}
        >
          <Typography
            sx={{ fontSize: 12 }}
            fontWeight="semi-bold"
            className="preview-dropdown-header"
          >
            Amenities & Location Details
            {visible && (
              <IconButton
                className="ml-4"
                onClick={() => {
                  dispatch(setActiveStep(0));
                  dispatch(resetApplicationSubmitted());
                  navigate("/stepper");
                }}
              >
                <ModeEditOutlineOutlinedIcon />
              </IconButton>
            )}
          </Typography>
          <IconButton aria-label="Collapse section" onClick={toggleExpand}>
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Box>

        {/* Content */}
        {isExpanded && ( // Conditionally render the content
          <CardContent>
            <Grid container spacing={4} justifyContent="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {detailsLayout?.map((row, rowIndex) => (
                    <Box
                      key={`row-${rowIndex}`}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 4,
                      }}
                    >
                      {row?.map((detail, colIndex) => (
                        <Box
                          key={`detail-${rowIndex}-${colIndex}`}
                          sx={{ flex: 1 }}
                        >
                          <Typography
                            className="preview-dropdown-label"
                            sx={{
                              fontWeight: 500,
                              mb: 0.5,
                              color: "#878787",
                            }}
                          >
                            {detail.label}
                          </Typography>
                          <Typography
                            sx={{ fontSize: "1rem" }}
                            className="preview-dropdown-value"
                          >
                            {detail.value !== undefined && detail.value !== null
                              ? detail.value
                              : "NA"}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                {latitude && (
                  <MapPanel
                    coordinates={mapCoordinates}
                    chainages={chainageDisplay}
                  />
                )}
              </Grid>
            </Grid>
          </CardContent>
        )}
      </Card>
    </Box>
  );
};
